<template>
  <div>
    <div
      v-if="mails.length"
      class="mt-3"
    >
      <b-list-group>
        <b-list-group-item
          v-for="(group, index) in mails"
          :key="`subject_${index}`"
          class="mb-2 p-0 border-0 shadow-sm"
        >
          <b-button
            v-b-toggle="'collapse-' + index"
            variant="light"
            class="w-100 text-left px-3 py-2 d-flex justify-content-between align-items-center"
            @click="toggleGroup(index)"
          >
            <div class="d-flex align-items-center">
              {{ group.displayLabel }}
            </div>

            <div class="d-flex align-items-center">
              <i
                :class="getHeaderIcon(group)"
                class="mr-2"
              />
              <i
                :class="[
                  'fa-fw fal',
                  openedGroups[index] ? 'fa-chevron-up' : 'fa-chevron-down'
                ]"
              />
            </div>
          </b-button>

          <b-collapse :id="'collapse-' + index">
            <ul class="list-unstyled mb-0 px-3 pb-1 pt-2">
              <li
                v-for="(mail, idx) in group.entries"
                :key="`mail_${index}_${idx}`"
                class="mb-3 pb-2 border-bottom"
              >
                <div class="d-flex justify-content-between align-items-center mb-1">
                  <div v-if="mail.processUUID">
                    <i
                      :id="mail.uuid"
                      :class="statusVariant(mail.deliveryStatus)"
                    />
                    <b-tooltip
                      v-if="mail.statusCode !== 250 && mail.deliveryStatus === 'failed'"
                      :target="mail.uuid"
                    >
                      {{ $t('processes.sidebar.mail_tab.SMTP.errorCode') }} {{ mail.statusCode }}: {{ mailErrorSMTP(mail.statusCode) }}
                    </b-tooltip>
                    <b-tooltip
                      v-else
                      :target="mail.uuid"
                    >
                      {{ $t(`processes.sidebar.mail_tab.deliveryStatuses.${mail.deliveryStatus}`) }}
                    </b-tooltip>
                    {{ formatDate(mail.created_at) }} {{ mail.recipient }}
                  </div>
                </div>
              </li>
            </ul>
          </b-collapse>
        </b-list-group-item>
      </b-list-group>
    </div>

    <div
      v-else
      class="text-muted mt-3 text-center"
    >
      {{ $t('processes.sidebar.mail_tab.subjectTemplates.noMails') }}
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import dayjs from 'dayjs';

export default {
  props: {
    uuid: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      openedGroups: {}
    };
  },
  computed: {
    ...mapGetters('mail', ['getMails', 'getLatestMailLogsRefresh']),

    mails() {
      const logs = this.getMails;
      const grouped = {};

      for (const mail of logs) {
        const displayLabel = this.getDisplayLabel(mail);
        if (!grouped[displayLabel]) {
          grouped[displayLabel] = { entries: [] };
        }
        grouped[displayLabel].entries.push(mail);
      }

      return Object.entries(grouped).map(([displayLabel, data]) => {
        const latestCreatedAt = Math.max(...data.entries.map(m => Number(m.created_at)));
        return {
          displayLabel,
          created_at: latestCreatedAt,
          entries: data.entries
        };
      });
    }
  },
  watch: {
    uuid: {
      immediate: true,
      handler() {
        this.$store.dispatch('mail/setMails', { mails: [] });
        this.refreshMails();
      }
    },
    getLatestMailLogsRefresh: {
      handler () {
        this.refreshMails()
      }
    }
  },
  methods: {
    ...mapActions('mail', ['loadMailLogs']),
    formatDate(timestamp) {
      return dayjs(Number(timestamp)).format('DD-MM-YYYY HH:mm');
    },
    refreshMails() {
      this.loadMailLogs({ processUUID: this.uuid });
    },
    statusVariant(status) {
      switch (status) {
        case 'accepted':
          return 'fa-regular fa-paper-plane';
        case 'delivered':
          return 'fa-regular fa-envelope-circle-check text-success';
        case 'failed':
          return 'fa-solid fa-triangle-exclamation text-danger';
        case 'opened':
          return 'fa-regular fa-envelope-open text-secondary';
        default:
          return 'secondary';
      }
    },
    mailErrorSMTP(errorCode) {
      const translationKey = `processes.sidebar.mail_tab.SMTP.${errorCode}`;
      const translated = this.$i18n.t(translationKey);

      if (translated !== translationKey) {
        return translated;
      }

      return this.$i18n.t('processes.sidebar.mail_tab.SMTP.fallback');
    },
    /**
     * Returns the display label for a mail group.
     * Prefers log_label in the user's locale, falls back to NL, then subject.
     */
    getDisplayLabel(mail) {
      const locale = this.$i18n.locale?.split('-')[0] || 'nl';
      if (mail.log_label) {
        return mail.log_label[locale] || mail.log_label.nl || mail.subject || this.$i18n.t('processes.sidebar.mail_tab.subjectTemplates.noSubject');
      }
      return mail.subject || this.$i18n.t('processes.sidebar.mail_tab.subjectTemplates.noSubject');
    },
    toggleGroup(index) {
      this.$set(this.openedGroups, index, !this.openedGroups[index]);
    },
    getHeaderIcon(group) {
      const statuses = group.entries.map(mail => mail.deliveryStatus);

      if (statuses.includes('failed')) {
        return 'fa-solid fa-triangle-exclamation text-danger';
      }

      if (statuses.some(status => status !== 'opened')) {
        return 'fa-regular fa-envelope-circle-check text-success';
      }

      return 'fa-regular fa-envelope-open text-secondary';
    }
  }
};
</script>

<style scoped>
.rotate-icon {
  transform: rotate(-90deg);
  transition: transform 0.3s ease;
}

.badge {
  line-height: unset;
}
</style>
